import { z } from 'zod'

import { PHONE_NUMBER_PATTERN } from '../../_utils/phone'

/**
 * A text field that must be filled in. Unlike bare `z.string()`, which happily accepts `''`, this
 * reports `message` for a missing, non-string, or empty value.
 */
export const requiredString = (message: string) => z.string({ error: message }).min(1, message)

/**
 * {@link requiredString}, with surrounding whitespace stripped before the emptiness check so a
 * field containing only spaces is treated as blank.
 */
export const requiredTrimmedString = (message: string) =>
  z.string({ error: message }).trim().min(1, message)

/**
 * An optional phone number: a blank field is fine, but anything typed in has to look like a phone
 * number.
 */
export const optionalPhoneNumber = (message: string) =>
  z.string().refine(value => !value.trim() || PHONE_NUMBER_PATTERN.test(value), message)

/**
 * A field whose value is an option object chosen from a picker (a color, font, placement, icon,
 * logo, …). The shape of the option is owned by the picker, so this only asserts that _something_
 * was selected, with the caller's translated message.
 */
export const requiredSelection = (message: string) => z.object({}, { error: message })

/**
 * A file input that must hold a `File`. Guarded on `File` itself because these schemas are built
 * during render, including server-side (Node exposes a global `File` from v20, but the guard keeps
 * the schema safe to construct in any environment).
 */
export const requiredFile = (message: string) =>
  z.custom<File>(value => typeof File !== 'undefined' && value instanceof File, message)
